import { LocateIcon, Mail, MapPin, Phone } from "lucide-react";

export const mockData = {
  about: {
    description: `
        Desarrollador de software con experiencia en la creación de aplicaciones web. Amplia capacidad de trabajo y
        adaptación a nuevos entornos, pensamiento analítico en respuesta a la resolución de problemas, trabajo de manera
        autónoma y rápida.
        He llevado a cabo proyectos que van desde el diseño y desarrollo hasta automatizaciones y
        despliegue del mismo.
    `,
    highlights: [
      "Experiencia sólida en Java y React",
      "Automatización y despliegue en la nube",
      "Manejo de base de datos relacionales en gran medida",
      "Pensamiento analítico enfocado la resolución de problemas",
      "Amplia adaptación a nuevas herramientas de trabajo"
    ]
  },

  navigator: {
    sections: [
      "index",
      "projects",
      "skills",
      "experience",
      "about",
      "certificates",
      "contact"
    ],
    items: [
      "Inicio",
      "Proyectos",
      "Tecnologías",
      "Experiencia",
      "Acerca de mí",
      "Certificados",
      "Contacto"
    ]
  },
  skills: {
    frontend: [
      { name: "React", level: 95 },
      { name: "RTK Query", level: 80 },
      { name: "React Query", level: 80 },
      { name: "TypeScript", level: 85 },
      { name: "R3F", level: 85 },
      { name: "Redux", level: 85 },
    ],
    backend: [
      { name: "Java 8~21", level: 90 },
      { name: "Java Reflection", level: 85 },
      { name: "Jakarta Servlets", level: 80 },
      { name: "Java Threads", level: 80 },
      { name: "Spring Framework", level: 90 },
      { name: "Apache Tomcat Embededd", level: 75 },
    ],
    database: [
      { name: "MySQL", level: 85 },
      { name: "PostgreSQL", level: 75 },
      { name: "Store Prodecures & Functions", level: 75 },
      { name: "Index & Views", level: 75 },
    ],
    cloud: [
      { name: "Amazon Web Services", level: 75 },
      { name: "S3 Bucket", level: 70 },
      { name: "EC2 Instance", level: 90 },
      { name: "RDS", level: 90 },
      { name: "Nginx", level: 95 },
      { name: "Docker", level: 95 },
    ],
    cicd: [
      { name: "Github Actions", level: 75 },
      { name: "Jobs and pipelines", level: 70 },
      { name: "Multiples workflows", level: 90 },
      { name: "Cron Jobs", level: 90 },
      { name: "Multiples artifacts", level: 95 },
    ],
    codeExamples: {
      frontend: {
        language: "tsx",
        code: `
  // Ejemplo de componente de Input para un formulario
const FieldComponent: FC<FieldTypeStruct> = ({ ...props }: FieldTypeStruct): ReactElement => {
    const { name, errors, placeholder, validators, register, title, type }: FieldTypeStruct = props;

    const entries: FieldValidationTypeStruct = 
    validators.reduce((previous: FieldValidationTypeStruct, current: FieldValidationTypeStruct): FieldValidationTypeStruct => {
        return { ...previous, ...current };
    });

    return (
        <FormControl>
            <FormLabel>{title}</FormLabel>
            <Input type={type} placeholder={placeholder} size="lg" {...register(name, entries)}
            />
            {<FormHelperText sx={{ fontSize: 13, color: "red" }}>{errors[name]?.message as string}</FormHelperText>}
        </FormControl>
    );
};

export default FieldComponent;
        `.trim()
      },
      backend: {
        language: "java",
        code: `
/* 
  Ejemplo de Java Reflection que utiliza Servlets
  y busca clases con la anotación @Controller a través de una interface 
*/
@Controller("/api")
public interface IRestController {
    @Path(value = "/rest", contentType = "application/json", method = HttpMethod.GET)
    <T> ResponseEntity<T> hello();

    @Path(value = "/test", contentType = "text/plain", method = HttpMethod.GET)
    <T> ResponseEntity<T> test();
}

@HandlesTypes(Controller.class)
public class ServletInitializer implements ServletContainerInitializer {
      @Override
      public void onStartup(Set<Class<?>> classes, ServletContext servletContext) throws ServletException {
          
          for (Class<?> clazz: classes) {
              try {
                  registerServlet(clazz, servletContext);
              } catch (Exception e) {
                  e.printStackTrace();
              }
          }
      }

      private <T> void registerServlet(final Class<T> clazz, final ServletContext servletContext) throws Exception {

          Controller controller = clazz.getDeclaredAnnotation(Controller.class);
          Class<?>[] contracts = clazz.getInterfaces();
          Method[] methods = clazz.getDeclaredMethods();

          ...

          // Register each class in servlet
          for (Class<? extends T> classImplements: classes) {
              T instance = classImplements.getConstructor().newInstance();
              RegisterServlet<T> registerServlet = new RegisterServlet<T>(instance);

              // Register servlet
              Dynamic dynamic = servletContext.addServlet(clazz.getSimpleName(), registerServlet);

              // Map methods to paths
              for (Method method: methods) {
                  ...
              }
          }
      }
  }
        `.trim()
      },
      database: {
        language: "sql",
        code: `
// Ejemplo de trigger en PostgreSQL que crea una cuenta antes de insertar un usuario
CREATE OR REPLACE FUNCTION fn_createAccountBeforeUser() RETURNS TRIGGER AS $$
  BEGIN
      INSERT INTO accounts DEFAULT VALUES;
      RETURN new;
  END;
$$ LANGUAGE plpgsql; 

CREATE OR REPLACE TRIGGER tr_createAccountBeforeUser
BEFORE INSERT ON users
FOR EACH ROW
EXECUTE FUNCTION fn_createAccountBeforeUser();
        `.trim()
      },
      cloud: {
        language: "yaml",
        code: `
  services:
    service-backend:
    image: cristianmorenoweb/salta-publica-backend:latest
    env_file: backend/.env
    depends_on:
      service-db:
        condition: service_healthy
        # restart: true
    ports:
      - 8080:8080
        `.trim()
      },
      cicd: {
        language: "yaml",
        code: `
 # Ejemplo de una automatización que compila una aplicación Java en Maven
 # y sube un artefacto con el JAR generado para ser descargado por otro workflow
 name: Upload to EC2 Instance

 on:
    workflow_call:

 jobs:
  build-app:
    runs-on: ubuntu-latest
    services:
      steps:
        - name: Checking repository...
          uses: actions/checkout@v4
        - name: Building app...
          run: |
            mvn -B package --file pom.xml
        - name: Running app...
          run: |
            java -jar target/app.jar
          env:
            spring.profiles.active: gh-action, dev
            DB_URL: jdbc:postgresql://127.0.0.1:5432/postgres?createDatabaseIfNotExist=true
        - name: Message success...
          run: |
            echo "App is working success!"
          - name: Uploading artifact...
            uses: actions/upload-artifact@v4
            with:
              name: app
              path: target/app.jar
    
  download-artifact:
    needs: [build-app]
    runs-on: ubuntu-latest
    steps:
      - id: download-artifact
        name: Downloading artifact...
        uses: actions/download-artifact@v4
        with:
          name: app
          path: app
        `.trim()
      }
    }
  },
  experience: [
    {
      position: "Backend Developer",
      company: "SaltaCoders",
      period: "Abr. 2024 - Feb. 2025",
      description: `
        Construí aplicaciones backend utilizando Spring Boot y Spring Security.
        Gestioné el proceso de automatización y despliegue de las aplicaciones.
        Utilicé PL/pgSQL para automatizar resultados de consultas SQL. 
        Utilicé autenticaciones basadas en OAuth 2.0 y OpenID.
      `,
      technologies: ["React", "Java", "Spring Boot", "Spring Security", "AWS", "Docker", "PostgreSQL"]
    },
    {
      position: "Fullstack Developer",
      company: "Munben",
      period: "Jun. 2023 - May. 2024",
      description: `
        Desarrollé aplicaciones web con React y NextJS.
        Utilicé Redux Toolkit y RTK Query como gestor de estados globales
        Utilicé MUI Joy para el manejo de componentes y estilos de la aplicación. 
        Securizé la aplicación a través de componentes, rutas y tokens JWT mediante SSR.
      `,
      technologies: ["React", "TypeScript", "Next", "Redux"]
    },
    {
      position: "Fullstack Developer",
      company: "IT-Tech GROUP",
      period: "Jun. 2022 - Ene. 2023",
      description: `
        Desarrollé aplicaciones web con React y NodeJS.
        Maqueté diseños complejos a través de CSS puro.
        Utilicé NodeMailer para enviar correos electrónicos.
        Utilicé contextos de React como gestor de estados globales.
      `,
      technologies: ["React", "NodeJS", "TypeScript", "Git"]
    },
    {
      position: "Freelance Developer",
      company: "AMXX",
      period: "Jun. 2019 - Ene. 2022",
      description: `
        Desarrollé plugins/scripts utilizando lenguaje Pawn para el motor GoldSrc.
        Utilicé MySQL y SQLite para almacenar estadísticas de usuarios.
        Gestioné recursos a través de macros y bits.
        Gestioné panel de control y archivos mediante FileZilla en conjunto con hPanel.
      `,
      technologies: ["Pawn", "MySQL", "FileZilla"]
    }
  ],
  
  projects: [
    {
      title: "Argensificados",
      description: `
        MVP destinada a crear clasificados con geolocalización dentro de Argentina. Está desplegada en
        una instancia de AWS a través de automatizaciones.`,
      technologies: ["React", "TypeScript", "Java", "Spring Boot", "Spring Security", "PostgreSQL", "AWS", "Github Actions", "Docker"],
      github: null,
      demo: "https://argensificados.com",
      image: [
        "argensificados.png"
      ]
    },
    {
      title: "Sistema Solar 3D",
      description: `
        Aplicación web que muestra un sistema solar en 3D hiperrealista con visibilidad de cada planeta y sus
        satélites más importantes. Utiliza texturas en HD y permite navegar entre planetas.
      `,
      technologies: ["React", "TypeScript", "React Leaflet", "Drei", "React Three Fiber"],
      github: null,
      demo: "https://space-solar-system.vercel.app/",
      image: [
        "solar_system.jpg"
      ]
    },
    {
      title: "Mercado Pago Java Anotaciones",
      description: `
        Librería desarrollada en Java que ofrece una integración más sencilla de Checkout Pro de
        Mercado Pago a través de anotaciones.
      `,
      technologies: ["Java", "Java Reflection", "Jakarta Servlets", "Java SPI", "Mercado Pago SDK"],
      github: "https://github.com/cscristianmoreno/mercadopago-annotations",
      demo: null,
      image: null
    },
    {
      title: "Java Simple Framework",
      description: `
        Librería desarrollada en Java que ofrece inyección de dependencias y métodos HTTP a
        través de anotaciones similar a lo que hace Spring Framework internamente.
      `,
      technologies: ["Java", "Java Reflection", "Jakarta Servlets", "Java SPI"],
      github: "https://github.com/cscristianmoreno/simple-framework",
      demo: null,
      image: null
    },
    {
      title: "Electronic Shop",
      description: `
        Comercio electrónico donde un usuario puede seleccionar productos en distintas categorías y comprar a través de una tarjeta de crédito
      `,
      technologies: ["React", "TypeScript", "MySQL", "Mercado Pago SDK"],
      github: "https://github.com/cscristianmoreno/react-ecommerce-mercadopago",
      demo: null,
      image: "electronic_shop.jpg"
    },
    {
      title: "Gestión de Stock",
      description: `
        Un sistema de gestión de stock que brinda diversidad de funcionalidades para controlar productos, usuarios, 
        clientes y mucho más, realizado con JavaFX, Spring Boot & Spring Security
      `,
      technologies: ["Java", "JavaFX", "Spring Boot", "Spring Security", "MySQL"],
      github: null,
      demo: null,
      image: "gestion_stock.jpg"
    },
    {
      title: "Portafolios 2023",
      description: `
        Portafolios 2023, desarrollada en Angular
      `,
      technologies: ["Angular", "HTML", "CSS", "TypeScript", "MySQL"],
      github: null,
      demo: "https://portafolios-2023.vercel.app/",
      image: "portafolios_2023.jpg"
    },
    {
      title: "Angular Chat Interactive",
      description: `
        Un simulador de mensajería el cual simula interacciones a través de usuarios de prueba por medio de la API (https://randomuser.me/)
      `,
      technologies: ["Angular", "HTML", "CSS", "TypeScript"],
      github: "https://angular-chat-interactive.vercel.app/",
      demo: "https://github.com/cscristianmoreno/angular_chat_interactive",
      image: "chat_interactive.jpg"
    }
  ],
  certificates: [
    {
      title: "Spring Security",
      issuer: "UDEMY",
      date: "Abril 2024 - Mayo 2024",
      url: "https://drive.google.com/file/d/1kcpESv1G56HWmzEM9OjI_e_p2fpJNiOw/view",
      image: "certificado_spring_security.jpg"
    },
    {
      title: "Abap Developer",
      issuer: "DL Consultores",
      date: "Enero 2024 - Marzo 2024",
      url: "https://drive.google.com/file/d/10cLBCtClrbM5lY2AE26Ud4GHToJuYbWs/view?pli=1",
      image: "certificado_abap_developer.jpg"
    },
    {
      title: "Java Developer",
      issuer: "Educacion IT",
      date: "Mayo 2023 - Noviembre 2023",
      url: "https://drive.google.com/file/d/19sDyHySQcmuD9pmyN42ZFwR3BxL0tnWV/view",
      image: "certificado_java_developer.jpg"
    }
  ],
  contact: {
    methods: [
      { type: "Email", value: "cristianmorenoweb@gmail.com", icon: <Mail className="h-8 w-8 text-blue-600 dark:text-blue-400" />, href: "mailto:cristianmorenoweb@gmail.com" },
      { type: "Teléfono", value: "+54 9 387 445-0711", icon: <Phone className="h-8 w-8 text-blue-600 dark:text-blue-400" />, href: "https://wa.me/+5493874450711" },
      { type: "Ubicación", value: "Salta, Capital (Argentina)", icon: <MapPin className="h-8 w-8 text-blue-600 dark:text-blue-400" />, href: null }
    ]
  }
};